import React from 'react';
import { motion } from 'framer-motion';

const Services = () => {
  const services = [
    {
      title: "Custom Solutions & Integrations",
      description: "Bespoke systems tailored to business workflows.",
      highlights: ["Custom apps", "APIs + integrations", "Data sync"],
      icon: (
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1H5a1 1 0 01-1-1v-3zM14 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1h-4a1 1 0 01-1-1v-3z" />
        </svg>
      ),
    },
    {
      title: "Systems Implementation",
      description: "Implementation, configuration, and rollout of internal systems.",
      highlights: ["CRM setup", "Permissions + environments", "Testing + docs"],
      icon: (
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      )
    },
    {
      title: "Workflow Automation",
      description: "Automation architecture across tools and platforms.",
      highlights: ["Cross-platform flows", "Event-driven logic", "Monitoring"],
      icon: (
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1H5a1 1 0 01-1-1v-3zM14 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1h-4a1 1 0 01-1-1v-3z" />
        </svg>
      )
    },
    {
      title: "Technical Consulting",
      description: "Audits, architecture guidance, and optimization roadmaps.",
      highlights: ["System reviews", "Reliability planning", "Tooling strategy"],
      icon: (
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      )
    },
    {
      title: "Operational Support",
      description: "Ongoing support, documentation, and user training.",
      highlights: ["Support + optimization", "SOPs", "Enablement"],
      icon: (
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      )
    }
  ];

  const ServiceCard = ({ service }) => {
    return (
      <div className="group relative overflow-hidden rounded-2xl border border-slate-200/70 bg-white p-6 transition hover:border-slate-300">
        {/* subtle top glow */}
        <div className="pointer-events-none absolute -top-20 left-1/2 h-40 w-[520px] -translate-x-1/2 rounded-full bg-slate-100/70 blur-3xl" />

        <div className="relative">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <motion.div 
                className="mt-0.5 rounded-xl border border-slate-200 bg-slate-50 p-2"
                whileHover={{ rotate: 360 }}
                transition={{ duration: 0.6, ease: "easeInOut" }}
              >
                <div className="text-slate-800">
                  {service.icon}
                </div>
              </motion.div>
              <div>
                <h3 className="text-base font-semibold tracking-tight text-slate-900">
                  {service.title}
                </h3>
                <p className="mt-1 text-sm leading-6 text-slate-600">
                  {service.description}
                </p>
              </div>
            </div>

            <svg className="h-5 w-5 text-slate-400 opacity-0 transition group-hover:opacity-100" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </div>

          {/* "pill" highlights */}
          <div className="mt-5 flex flex-wrap gap-2">
            {service.highlights.map((highlight, idx) => (
              <span
                key={idx}
                className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-700"
              >
                {highlight}
              </span>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <section id="services" className="border-t border-slate-200/60 bg-soft-surface py-16 md:py-24 px-6 md:px-10">
      <div className="mx-auto max-w-6xl">
        <div>
          <p className="text-xs font-medium tracking-[0.18em] text-text-secondary">
            WHAT WE DO
          </p>
          <h2 className="mt-3 text-3xl font-semibold tracking-tight text-text-primary md:text-4xl">
            Services
          </h2>
          <p className="mt-3 max-w-xl text-base leading-7 text-text-secondary">
            Implementation-focused consulting across systems, automation, and custom integrations.
          </p>
        </div>

        {/* Uniform grid */}
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
          {services.map((service) => (
            <ServiceCard key={service.title} service={service} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
